import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Eye,
  FileText,
  Loader2,
  LogOut,
  Save,
  Search,
  SendHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import type { EvaluatorAssignmentSummary, EvaluatorDecision, RubricCriterionKey } from '@/api/evaluator-portal';
import { getEvaluatorDocument } from '@/api/evaluator-portal';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import {
  useEvaluatorPortalAssignmentDetail,
  useEvaluatorPortalAssignments,
  useEvaluatorPortalEvaluation,
  useSaveEvaluatorDraft,
  useSubmitEvaluatorEvaluation,
} from '@/hooks/use-evaluator-portal';
import { EvaluatorRubricDetailModal } from './EvaluatorRubricDetailModal';
import {
  computeRubricTotal,
  countCompletedCriteria,
  deriveVisualDecision,
  EMPTY_RUBRIC_DATA,
  EVALUATOR_RUBRIC_CRITERIA,
  EVALUATOR_RUBRIC_SCORES,
  hasCompleteRubric,
  mergeRubricData,
  type RubricSelectionState,
} from './rubric-definition';

interface EvaluatorPortalProps {
  token: string;
  onBackToRegister: () => void;
}

type PortalView = 'list' | 'evaluation';
type AssignmentFilter = 'ALL' | 'PENDING' | 'DRAFT' | 'UNDER_REVIEW' | 'SUBMITTED' | 'SECOND_ROUND';

interface DocumentState {
  loading: boolean;
  url: string | null;
  status: number | null;
  message: string | null;
}

interface ApiErrorShape {
  status: number;
  message: string;
}

const FILTER_OPTIONS: Array<{ key: AssignmentFilter; label: string }> = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Pendiente' },
  { key: 'DRAFT', label: 'En borrador' },
  { key: 'UNDER_REVIEW', label: 'En evaluación' },
  { key: 'SUBMITTED', label: 'Enviada' },
  { key: 'SECOND_ROUND', label: 'Segunda ronda' },
];

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDecisionLabel(decision: EvaluatorDecision | null): string {
  if (decision === 'APPROVED') return 'Aprobado';
  if (decision === 'OBSERVATIONS') return 'Observaciones';
  if (decision === 'REJECTED') return 'Rechazado';
  return 'Pendiente';
}

function getDecisionBadgeClass(decision: EvaluatorDecision | null): string {
  if (decision === 'APPROVED') return 'border-emerald-200 bg-emerald-100 text-emerald-800';
  if (decision === 'OBSERVATIONS') return 'border-amber-200 bg-amber-100 text-amber-800';
  if (decision === 'REJECTED') return 'border-rose-200 bg-rose-100 text-rose-800';
  return 'border-slate-200 bg-slate-100 text-slate-700';
}

async function resolveApiError(error: unknown, fallback: string): Promise<ApiErrorShape> {
  const maybe = error as {
    response?: {
      status?: number;
      data?: unknown;
    };
    message?: string;
  };

  const status = Number(maybe?.response?.status || 0);
  const data = maybe?.response?.data;

  if (typeof data === 'object' && data !== null && !(data instanceof Blob)) {
    const maybeMessage = (data as { message?: string; error?: { message?: string } }).message
      || (data as { error?: { message?: string } }).error?.message;
    if (maybeMessage) {
      return { status, message: maybeMessage };
    }
  }

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string; error?: { message?: string } };
      const maybeMessage = parsed.message || parsed.error?.message;
      if (maybeMessage) {
        return { status, message: maybeMessage };
      }
    } catch {
      // noop
    }
  }

  return {
    status,
    message: maybe?.message || fallback,
  };
}

function buildStatusMeta(
  assignment: EvaluatorAssignmentSummary,
  completedCriteria: number,
): { filter: AssignmentFilter; label: string; className: string } {
  if (assignment.isSubmitted) {
    return {
      filter: 'SUBMITTED',
      label: 'Enviada',
      className: 'border-emerald-200 bg-emerald-100 text-emerald-800',
    };
  }

  if (assignment.roundNumber === 2) {
    return {
      filter: 'SECOND_ROUND',
      label: 'Segunda ronda',
      className: 'border-sky-200 bg-sky-100 text-sky-800',
    };
  }

  if (assignment.ownEvaluationStatus === 'DRAFT' && completedCriteria >= EVALUATOR_RUBRIC_CRITERIA.length) {
    return {
      filter: 'UNDER_REVIEW',
      label: 'En evaluación',
      className: 'border-amber-200 bg-amber-100 text-amber-800',
    };
  }

  if (assignment.ownEvaluationStatus === 'DRAFT') {
    return {
      filter: 'DRAFT',
      label: 'En borrador',
      className: 'border-slate-200 bg-slate-200 text-slate-700',
    };
  }

  return {
    filter: 'PENDING',
    label: 'Pendiente',
    className: 'border-yellow-200 bg-yellow-100 text-yellow-800',
  };
}

function getActionLabel(filter: AssignmentFilter): string {
  if (filter === 'SUBMITTED') return 'Ver dictamen';
  if (filter === 'DRAFT' || filter === 'UNDER_REVIEW') return 'Continuar';
  return 'Evaluar';
}

function getDocumentLabel(assignment: EvaluatorAssignmentSummary): string {
  return assignment.hasEvaluableDocument ? 'PDF evaluable' : 'Sin PDF';
}

export function EvaluatorPortal({ token, onBackToRegister }: EvaluatorPortalProps) {
  const [portalView, setPortalView] = useState<PortalView>('list');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AssignmentFilter>('ALL');
  const [rubricData, setRubricData] = useState<RubricSelectionState>(EMPTY_RUBRIC_DATA);
  const [observationComment, setObservationComment] = useState('');
  const [evaluatedPaperVersionId, setEvaluatedPaperVersionId] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [documentState, setDocumentState] = useState<DocumentState>({
    loading: false,
    url: null,
    status: null,
    message: null,
  });

  const assignmentsQuery = useEvaluatorPortalAssignments(token);
  const assignments = assignmentsQuery.data ?? [];

  useEffect(() => {
    if (!selectedAssignmentId && assignments[0]?.assignmentId) {
      setSelectedAssignmentId(assignments[0].assignmentId);
    }
  }, [assignments, selectedAssignmentId]);

  const detailQuery = useEvaluatorPortalAssignmentDetail(token, selectedAssignmentId);
  const evaluationQuery = useEvaluatorPortalEvaluation(token, selectedAssignmentId);
  const saveDraftMutation = useSaveEvaluatorDraft(token, selectedAssignmentId);
  const submitMutation = useSubmitEvaluatorEvaluation(token, selectedAssignmentId);

  const selectedAssignment = useMemo(
    () => assignments.find((item) => item.assignmentId === selectedAssignmentId) ?? assignments[0] ?? null,
    [assignments, selectedAssignmentId],
  );

  const currentEvaluation = evaluationQuery.data ?? null;
  const currentDetail = detailQuery.data ?? null;

  useEffect(() => {
    if (!currentEvaluation) return;
    setRubricData(mergeRubricData(currentEvaluation.rubricData));
    setObservationComment(currentEvaluation.observationComment ?? '');
    setEvaluatedPaperVersionId(currentEvaluation.evaluatedPaperVersionId ?? '');
    setFeedbackMessage(null);
  }, [
    selectedAssignmentId,
    currentEvaluation?.status,
    currentEvaluation?.isSubmitted,
    currentEvaluation?.observationComment,
    currentEvaluation?.evaluatedPaperVersionId,
    currentEvaluation?.submittedAt,
    currentEvaluation?.totalScore,
  ]);

  useEffect(() => {
    let revokedUrl: string | null = null;
    let cancelled = false;

    if (!token || !selectedAssignmentId || portalView !== 'evaluation') {
      setDocumentState((prev) => {
        if (prev.url) {
          URL.revokeObjectURL(prev.url);
        }
        return { loading: false, url: null, status: null, message: null };
      });
      return undefined;
    }

    const loadDocument = async () => {
      setDocumentState((prev) => {
        if (prev.url) {
          URL.revokeObjectURL(prev.url);
        }
        return { loading: true, url: null, status: null, message: null };
      });

      try {
        const blob = await getEvaluatorDocument(token, selectedAssignmentId);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        setDocumentState({
          loading: false,
          url,
          status: null,
          message: null,
        });
      } catch (error) {
        const resolved = await resolveApiError(error, 'No fue posible cargar el documento evaluable.');
        if (cancelled) return;
        setDocumentState({
          loading: false,
          url: null,
          status: resolved.status,
          message: resolved.message,
        });
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [portalView, selectedAssignmentId, token]);

  const completedCriteria = useMemo(() => countCompletedCriteria(rubricData), [rubricData]);
  const totalScore = useMemo(() => computeRubricTotal(rubricData), [rubricData]);
  const completeRubric = useMemo(() => hasCompleteRubric(rubricData), [rubricData]);
  const visualDecision = useMemo(
    () => deriveVisualDecision(totalScore, completeRubric),
    [completeRubric, totalScore],
  );
  const requiresObservation = visualDecision === 'OBSERVATIONS';
  const isReadOnly = currentEvaluation?.isSubmitted ?? false;
  const isSecondRoundBlocked = currentDetail?.roundNumber === 2 && visualDecision === 'OBSERVATIONS';
  const hasSubmittedObservation = Boolean(currentEvaluation?.observationComment?.trim());
  const shouldShowObservationField = requiresObservation || (isReadOnly && hasSubmittedObservation);
  const submitButtonLabel = isReadOnly
    ? 'Dictamen enviado'
    : visualDecision === 'APPROVED'
      ? 'Enviar dictamen aprobado'
      : visualDecision === 'OBSERVATIONS'
        ? 'Enviar dictamen con observaciones'
        : visualDecision === 'REJECTED'
          ? 'Enviar dictamen de rechazo'
          : 'Enviar dictamen';

  const filteredAssignments = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return assignments.filter((assignment) => {
      const relatedCompletedCriteria = assignment.assignmentId === selectedAssignmentId
        ? completedCriteria
        : 0;
      const statusMeta = buildStatusMeta(assignment, relatedCompletedCriteria);

      const matchesFilter = filter === 'ALL' || statusMeta.filter === filter;
      const matchesSearch = !normalizedSearch
        || assignment.folio.toLowerCase().includes(normalizedSearch)
        || String(assignment.tituloContribucion || '').toLowerCase().includes(normalizedSearch);

      return matchesFilter && matchesSearch;
    });
  }, [assignments, completedCriteria, filter, search, selectedAssignmentId]);

  const pendingCount = useMemo(
    () => assignments.filter((assignment) => !assignment.isSubmitted).length,
    [assignments],
  );

  const currentStatusMeta = selectedAssignment
    ? buildStatusMeta(selectedAssignment, selectedAssignment.assignmentId === selectedAssignmentId ? completedCriteria : 0)
    : null;

  const handleSelectScore = (criterionKey: RubricCriterionKey, score: number) => {
    if (isReadOnly) return;
    setRubricData((prev) => ({
      ...prev,
      [criterionKey]: prev[criterionKey] === score ? null : score,
    }));
  };

  const handleOpenAssignment = (assignmentId: string) => {
    setSelectedAssignmentId(assignmentId);
    setPortalView('evaluation');
    setFeedbackMessage(null);
  };

  const handleSaveDraft = async () => {
    if (!selectedAssignmentId) return;

    try {
      setFeedbackMessage(null);
      await saveDraftMutation.mutateAsync({
        rubricData,
        observationComment: observationComment.trim() || undefined,
        evaluatedPaperVersionId: evaluatedPaperVersionId || undefined,
      });
      toast.success('Borrador guardado correctamente.');
    } catch (error) {
      const resolved = await resolveApiError(error, 'No fue posible guardar el borrador.');
      setFeedbackMessage(resolved.message);
      toast.error('Error al guardar borrador', { description: resolved.message });
    }
  };

  const handleSubmit = async () => {
    if (!selectedAssignmentId) return;

    if (!completeRubric) {
      const message = 'Debe capturar los 6 criterios antes de enviar el dictamen.';
      setFeedbackMessage(message);
      toast.error(message);
      return;
    }

    if (!evaluatedPaperVersionId) {
      const message = 'No existe un PDF evaluable asociado para enviar el dictamen.';
      setFeedbackMessage(message);
      toast.error(message);
      return;
    }

    if (requiresObservation && !observationComment.trim()) {
      const message = 'La observación es obligatoria cuando el resultado cae en observaciones.';
      setFeedbackMessage(message);
      toast.error(message);
      return;
    }

    if (isSecondRoundBlocked) {
      const message = 'En segunda ronda no se permite enviar un resultado con observaciones.';
      setFeedbackMessage(message);
      toast.error(message);
      return;
    }

    const payload = EVALUATOR_RUBRIC_CRITERIA.reduce((acc, criterion) => {
      acc[criterion.key] = Number(rubricData[criterion.key]);
      return acc;
    }, {} as Record<RubricCriterionKey, number>);

    try {
      const result = await submitMutation.mutateAsync({
        rubricData: payload,
        observationComment: observationComment.trim() || undefined,
        evaluatedPaperVersionId,
      });
      setFeedbackMessage(result.message);
      toast.success(result.message, {
        description: `Decisión final: ${getDecisionLabel(result.decision)} (${result.totalScore}/30).`,
      });
    } catch (error) {
      const resolved = await resolveApiError(error, 'No fue posible enviar el dictamen.');
      setFeedbackMessage(resolved.message);
      toast.error('Error al enviar dictamen', { description: resolved.message });
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <Card className="mx-auto max-w-2xl border-t-4 border-t-red-500">
          <CardHeader>
            <CardTitle className="text-red-700">Acceso no disponible</CardTitle>
            <CardDescription>Portal del evaluador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-red-700">
            <p>El enlace del evaluador no contiene un token valido.</p>
            <Button type="button" variant="outline" onClick={onBackToRegister}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (assignmentsQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="flex items-center gap-3 text-[#0f2744]">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg font-semibold">Cargando portal del evaluador...</span>
        </div>
      </div>
    );
  }

  if (assignmentsQuery.isError) {
    const message = 'No fue posible validar el acceso al portal del evaluador.';
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <Card className="mx-auto max-w-2xl border-t-4 border-t-red-500">
          <CardHeader>
            <CardTitle className="text-red-700">Acceso no disponible</CardTitle>
            <CardDescription>Portal del evaluador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-red-700">
            <p>{message}</p>
            <Button type="button" variant="outline" onClick={onBackToRegister}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <Card className="mx-auto max-w-3xl border border-slate-200">
          <CardHeader>
            <CardTitle className="text-[#0f2744]">Sin asignaciones activas</CardTitle>
            <CardDescription>El enlace ya no tiene una asignación utilizable en este momento.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" onClick={onBackToRegister}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen">
        <aside className="hidden w-72 flex-col bg-[#0f2744] text-white lg:flex">
          <div className="border-b border-[#1e3a5f] px-8 py-8">
            <p className="text-5xl font-black tracking-wide">UNACH</p>
            <p className="mt-2 text-2xl font-medium text-[#c9a227]">Evaluador de ponencias</p>
          </div>

          <nav className="flex-1 px-4 py-10">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-r-xl border-l-4 border-[#c9a227] bg-[#17345b] px-5 py-4 text-left text-lg font-semibold"
              onClick={() => setPortalView('list')}
            >
              <ClipboardList className="h-5 w-5 text-[#c9a227]" />
              Mis asignaciones
            </button>
          </nav>

          <div className="border-t border-[#1e3a5f] px-8 py-6 text-sm text-slate-300">
            v2.1.0 UNACH System
          </div>
        </aside>

        <div className="flex min-h-screen flex-1 flex-col">
          <header className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Portal del evaluador</p>
                <h1 className="text-2xl font-bold text-[#0f2744]">Evaluación de Ponencias</h1>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  Evento: {selectedAssignment?.eventName || 'Sin evento'}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  {currentDetail?.evaluator.name || 'Evaluador'}
                </span>
                <Button type="button" variant="ghost" className="gap-2" onClick={onBackToRegister}>
                  <LogOut className="h-4 w-4" />
                  Salir
                </Button>
              </div>
            </div>
          </header>

          <main className="flex-1 p-4 lg:p-8">
            {portalView === 'list' ? (
              <div className="space-y-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-4xl font-bold text-[#0f2744]">Mis asignaciones</h2>
                    <p className="mt-2 text-base text-slate-600">Revise y dictamine las propuestas asignadas a su cargo.</p>
                  </div>
                  <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm">
                    Pendientes: {pendingCount}
                  </div>
                </div>

                <Card className="border border-slate-200 shadow-sm">
                  <CardContent className="space-y-5 p-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="relative w-full max-w-xl">
                        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          className="pl-9"
                          placeholder="Buscar por folio o titulo de la ponencia..."

                        />
                      </div>

                      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1">
                        {FILTER_OPTIONS.map((option) => (
                          <Button
                            key={option.key}
                            type="button"
                            size="sm"
                            onClick={() => setFilter(option.key)}
                            className={filter === option.key ? 'bg-[#0f2744] text-white hover:bg-[#17345b]' : 'bg-transparent text-slate-700 hover:bg-white'}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b-2 border-[#c9a227]">
                            <TableHead>Folio</TableHead>
                            <TableHead>Titulo de ponencia</TableHead>
                            <TableHead>Ronda</TableHead>
                            <TableHead>Documento</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAssignments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                                No hay asignaciones que coincidan con el filtro actual.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAssignments.map((assignment) => {
                              const relatedCompletedCriteria = assignment.assignmentId === selectedAssignmentId
                                ? completedCriteria
                                : 0;
                              const statusMeta = buildStatusMeta(assignment, relatedCompletedCriteria);
                              return (
                                <TableRow key={assignment.assignmentId} className="hover:bg-slate-50">
                                  <TableCell>
                                    <span className="inline-flex rounded-lg bg-slate-100 px-3 py-2 font-semibold text-[#0f2744]">
                                      {assignment.folio}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <p className="max-w-xl text-base font-semibold text-slate-900">
                                      {assignment.tituloContribucion || 'Ponencia sin título'}
                                    </p>
                                  </TableCell>
                                  <TableCell>Ronda {assignment.roundNumber}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center gap-2 text-slate-600">
                                      <FileText className="h-4 w-4" />
                                      {getDocumentLabel(assignment)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${statusMeta.className}`}>
                                      {statusMeta.label}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      type="button"
                                      onClick={() => handleOpenAssignment(assignment.assignmentId)}
                                      className={statusMeta.filter === 'SUBMITTED' ? 'border border-[#0f2744] bg-white text-[#0f2744] hover:bg-slate-50' : 'bg-[#0f2744] text-white hover:bg-[#17345b]'}
                                    >
                                      {getActionLabel(statusMeta.filter)}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="space-y-6">
                <Card className="border border-slate-200 shadow-sm">
                  <CardContent className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-2">
                      <Button type="button" variant="ghost" className="-ml-3 gap-2 text-[#0f2744]" onClick={() => setPortalView('list')}>
                        <ArrowLeft className="h-4 w-4" />
                        Volver
                      </Button>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Revisando ponencia</p>
                        <h2 className="text-xl font-bold text-[#0f2744]">
                          {selectedAssignment?.folio || '-'} - {selectedAssignment?.tituloContribucion || 'Ponencia sin título'}
                        </h2>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                      Ronda: {currentDetail?.roundNumber || selectedAssignment?.roundNumber || 1}
                    </div>
                  </CardContent>
                </Card>

                {feedbackMessage ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {feedbackMessage}
                  </div>
                ) : null}

                <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.85fr)_500px] 2xl:grid-cols-[minmax(0,2fr)_540px]">
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-2xl text-[#0f2744]">Documento evaluable</CardTitle>
                      <CardDescription>Solo se muestra el PDF permitido por el flujo de evaluación ciega.</CardDescription>
                    </CardHeader>
                    <CardContent className="pb-5">
                      {documentState.loading ? (
                        <div className="flex h-[78vh] min-h-[760px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-500 xl:h-[calc(100vh-15rem)] xl:min-h-[820px]">
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Cargando documento evaluable...
                        </div>
                      ) : documentState.url ? (
                        <iframe
                          title="Documento evaluable"
                          src={documentState.url}
                          className="h-[78vh] min-h-[760px] w-full rounded-xl border border-slate-200 bg-white xl:h-[calc(100vh-15rem)] xl:min-h-[820px]"
                        />
                      ) : (
                        <div className="flex h-[78vh] min-h-[760px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-slate-600 xl:h-[calc(100vh-15rem)] xl:min-h-[820px]">
                          <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
                          <p className="text-base font-semibold">No hay PDF evaluable disponible</p>
                          <p className="mt-2 max-w-lg text-sm">
                            {documentState.message || 'La ponencia no cuenta con un documento PDF evaluable para este portal.'}
                          </p>
                          {documentState.status === 422 ? (
                            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-amber-700">Error 422 - Documento no PDF</p>
                          ) : null}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <div className="space-y-4 xl:sticky xl:top-6 xl:max-h-[calc(100vh-9rem)] xl:overflow-y-auto xl:pr-1">
                    <Card className="border border-slate-200 shadow-sm">
                      <CardHeader className="space-y-3 pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <CardTitle className="text-2xl text-[#0f2744]">Calificación</CardTitle>
                            <CardDescription>Rúbrica simple interactiva</CardDescription>
                          </div>
                          <Button type="button" variant="outline" onClick={() => setDetailModalOpen(true)}>
                            Ver detalle rúbrica
                          </Button>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[42%]">Criterio</TableHead>
                                {EVALUATOR_RUBRIC_SCORES.map((score) => (
                                  <TableHead key={score} className="w-[11%] text-center">{score}</TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {EVALUATOR_RUBRIC_CRITERIA.map((criterion) => (
                                <TableRow key={criterion.key}>
                                  <TableCell className="font-medium text-[#0f2744]">{criterion.label}</TableCell>
                                  {EVALUATOR_RUBRIC_SCORES.map((score) => {
                                    const selected = rubricData[criterion.key] === score;
                                    return (
                                      <TableCell key={`${criterion.key}-${score}`} className="p-2 text-center">
                                        <button
                                          type="button"
                                          disabled={isReadOnly}
                                          onClick={() => handleSelectScore(criterion.key, score)}
                                          className={`h-10 w-10 rounded-md border text-sm font-semibold transition ${selected
                                            ? 'border-emerald-500 bg-emerald-100 text-emerald-900'
                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                            } ${isReadOnly ? 'cursor-default' : ''}`}
                                        >
                                          {score}
                                        </button>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                          <div className="grid gap-1">
                            <p className="font-semibold text-[#0f2744]">Escala</p>
                            <p>5. Excelente</p>
                            <p>4. Bueno</p>
                            <p>3. Suficiente</p>
                            <p>2. Deficiente</p>
                            <p>1. Muy deficiente</p>
                          </div>

                          <div className="space-y-2 rounded-lg bg-white p-4 shadow-sm">
                            <p className="text-sm font-semibold text-slate-500">Calificación actual</p>
                            <p className="text-3xl font-bold text-[#0f2744]">{totalScore} / 30</p>
                            <p className="text-sm text-slate-600">Criterios completos: {completedCriteria} de {EVALUATOR_RUBRIC_CRITERIA.length}</p>
                            {visualDecision ? (
                              <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-sm font-semibold ${getDecisionBadgeClass(visualDecision)}`}>
                                Resultado visual: {getDecisionLabel(visualDecision)}
                              </span>
                            ) : (
                              <span className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                                Complete los 6 criterios para ver el resultado visual
                              </span>
                            )}
                          </div>
                        </div>

                        {isSecondRoundBlocked ? (
                          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                            En segunda ronda no se permite enviar un resultado en observaciones. Ajuste la rúbrica antes de enviar.
                          </div>
                        ) : null}

                        {shouldShowObservationField ? (
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-[#0f2744]">Observaciones{requiresObservation ? ' *' : ''}</label>
                            <Textarea
                              value={observationComment}
                              onChange={(event) => setObservationComment(event.target.value)}
                              disabled={isReadOnly}
                              rows={6}
                              placeholder="Escriba las observaciones para el dictamen cuando correspondan."
                            />
                            <p className="text-xs text-slate-500">
                              El backend calcula la decisión final. Este campo se vuelve obligatorio cuando el resultado visual cae en observaciones.
                            </p>
                            {requiresObservation ? (
                              <p className="text-xs font-medium text-[#0f2744]">
                                Las observaciones serán consideradas en la consolidación final del comité.
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                          <p className="font-semibold text-[#0f2744]">Estado actual</p>
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            {currentStatusMeta ? (
                              <span className={`inline-flex rounded-full border px-3 py-1 font-semibold ${currentStatusMeta.className}`}>
                                {currentStatusMeta.label}
                              </span>
                            ) : null}
                            {currentEvaluation?.decision ? (
                              <span className={`inline-flex rounded-full border px-3 py-1 font-semibold ${getDecisionBadgeClass(currentEvaluation.decision)}`}>
                                Decisión backend: {getDecisionLabel(currentEvaluation.decision)}
                              </span>
                            ) : null}
                            {isReadOnly ? (
                              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-semibold text-emerald-800">
                                <CheckCircle2 className="h-4 w-4" />
                                Solo lectura
                              </span>
                            ) : null}
                          </div>
                          {currentEvaluation?.submittedAt ? (
                            <p className="mt-3 text-xs text-slate-500">Enviada el {formatDateTime(currentEvaluation.submittedAt)}</p>
                          ) : null}
                        </div>

                        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                          <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={isReadOnly || saveDraftMutation.isPending || submitMutation.isPending}>
                            {saveDraftMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Guardar borrador
                          </Button>
                          <Button type="button" className="bg-[#0f2744] text-white hover:bg-[#17345b]" onClick={handleSubmit} disabled={isReadOnly || submitMutation.isPending || saveDraftMutation.isPending}>
                            {submitMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <SendHorizontal className="mr-2 h-4 w-4" />}
                            {submitButtonLabel}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border border-slate-200 shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-xl text-[#0f2744]">Detalle de asignación</CardTitle>
                        <CardDescription>Vista ciega del dictamen individual.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-slate-700">
                        <div className="flex items-center gap-3">
                          <Eye className="h-4 w-4 text-[#0f2744]" />
                          <span>Folio: <strong>{selectedAssignment?.folio || '-'}</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-[#0f2744]" />
                          <span>Documento: <strong>{selectedAssignment ? getDocumentLabel(selectedAssignment) : 'Sin documento'}</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                          <ClipboardList className="h-4 w-4 text-[#0f2744]" />
                          <span>Versión evaluada: <strong>{evaluatedPaperVersionId || 'No resuelta'}</strong></span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <EvaluatorRubricDetailModal
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        rubricData={rubricData}
        readOnly={isReadOnly}
        onSelect={handleSelectScore}
      />
    </div>
  );
}


